import {
  BrowserCacheLocation,
  LogLevel,
  PublicClientApplication,
  type Configuration,
  type RedirectRequest,
} from '@azure/msal-browser';

import { buildAadPublicCloudDiscoveryMetadata, buildAadV2AuthorityMetadata } from './aad-metadata';

// Single-tenant + PKCE — no client secret on the SPA. The Entra app registration
// must list `${origin}/auth/callback` and `${origin}/` as SPA redirect URIs.
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID;
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE;

if (!tenantId || !clientId || !apiScope) {
  throw new Error('Missing VITE_ENTRA_* env vars — see apps/web/.env.example');
}

export const ENTRA_API_SCOPE = apiScope;

const authority = `https://login.microsoftonline.com/${tenantId}`;

// Feed MSAL the (static, standard) AAD v2.0 authority + cloud-discovery metadata
// so it can skip its two pre-redirect network round-trips and navigate to Entra
// immediately when the user clicks "sign in". See aad-metadata.ts for the why +
// verification; the shapes are pinned by aad-metadata.test.ts.
const cloudDiscoveryMetadata = buildAadPublicCloudDiscoveryMetadata(tenantId);
const authorityMetadata = buildAadV2AuthorityMetadata(tenantId);

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    redirectUri: `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri: `${window.location.origin}/login`,
    cloudDiscoveryMetadata,
    authorityMetadata,
  },
  cache: {
    // sessionStorage scopes the cache to the browser tab — closing the tab forces
    // re-auth. Safer than localStorage for access-token storage.
    cacheLocation: BrowserCacheLocation.SessionStorage,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (import.meta.env.DEV && level <= LogLevel.Warning) {
          console.log(`[MSAL ${LogLevel[level]}] ${message}`);
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

export const loginRequest: RedirectRequest = {
  scopes: [apiScope, 'openid', 'profile', 'email'],
};

export const tokenRequestScopes: string[] = [apiScope];

export const msalInstance = new PublicClientApplication(msalConfig);
